class NarvarOrderTracker extends HTMLElement {
  constructor() {
    super();
    this.loadNarvarSdk();
    this.validations = this.querySelectorAll('narvar-order-tracker-validation');
    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.submitBtn = this.form.querySelector('button[type="submit"]');
    this.retailer = this.getAttribute('retailer');
    this.localeLanguage = this.getAttribute('locale-language');
    this.localeCountry = this.getAttribute('locale-country');
    this.webapiStore = this.getAttribute('webapi-store');
    this.versionName = this.getAttribute('version-name');
    this.queryParams = window.location.search
      .replace(/^\?/g, '')
      .split('&')
      .reduce(function (acc, param) {
        var keyAndValue = param.split('=');
        acc[keyAndValue[0]] = keyAndValue[1];
        return acc;
      }, {});
    this.orderNumberField = this.form.querySelector('#order-number');
    this.emailField = this.form.querySelector('#email');
    this.buttonRefreshElement = this.querySelector('#button-refresh');
    this.trackErrorElement = this.querySelector('#track-error');
    this.trackInfoElement = this.querySelector('#track-info');
    this.narvarTrackElement = this.querySelector('[data-narvar-feature="track"]');
    this.narvarTrackContainerElement = this.querySelector('#narvar-track-container');
    this.loadingElement = this.querySelector('#thirdLottie');
    this.genericOrderValidator = this.querySelector('narvar-order-tracker-validation.generic-order-validator');
    this.orderValidator = this.querySelector('narvar-order-tracker-validation.order-validator');
    if (this.validateQueryParams()) {
      this.startLoading();
    }
    this.addEventListener('sdk:loaded', () => this.onQueryParamsAdded());

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Check if nodes were added
          for (const addedNode of mutation.addedNodes) {
            // Check if the added node is an element node
            if (addedNode.nodeType === 1 && addedNode.nodeName === 'IFRAME') {
              // const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              // addedNode.contentWindow.onload = () => {
              //   this.stopLoading();
              // };
              setTimeout(() => this.stopLoading(), 500);
            }
          }
        }
      }
    });

    observer.observe(this.narvarTrackElement, { childList: true });

    window.addEventListener('DOMContentLoaded', () => {
      if (this.queryParams.order_number) {
        this.orderNumberField.value = this.queryParams.order_number;
      }

      if (this.orderNumberField.value !== '' && this.emailField.value !== '') {
        this.submitBtn.click();
      }
    });
  }

  loadNarvarSdk() {
    if (this.sdkLoaded) {
      return;
    }

    const sdk = this.getAttribute('sdk');
    const scriptElement = document.createElement('script');
    scriptElement.src = sdk;
    scriptElement.onload = () => {
      this.dispatchEvent(new CustomEvent('sdk:loaded'));
      this.sdkLoaded = true;
    };
    document.head.appendChild(scriptElement);
  }

  validate(orderNumber) {
    if (!this.genericOrderValidator.validate(orderNumber)) {
      this.genericOrderValidator.showMsg();
      return false;
    }

    if (!this.orderValidator.validate(orderNumber)) {
      this.orderValidator.showMsg();
      return false;
    }

    return true;
  }

  async onSubmitHandler(evt) {
    evt.preventDefault();
    this.resetMessages();

    const orderNumber = this.orderNumberField.value.replace(/\s/g, '');
    const email = this.emailField.value.replace(/\s/g, '');

    if (!this.validate(orderNumber)) {
      return;
    }

    try {
      this.startLoading();
      const response = await fetch(
        `https://web-api.representclo.com/order-tracking/?store=${this.webapiStore}&order_name=${orderNumber}&email=${email}`,
      );
      const result = await response.json();

      if (!result.success) {
        this.updateErrorMessage('Unable to track your order. Please check your order number/email address.');
        this.hideNarvarWidget();
        this.stopLoading();
        return;
      }

      if (result.fulfillments.length === 0) {
        this.updateInfoMessage('Your order is currently being processed and will be on its way to you soon.');
        this.hideNarvarWidget();
        this.stopLoading();
        return;
      }

      const fulfillment = result.fulfillments[0];
      const options = {
        retailer: this.retailer,
        locale_language: this.queryParams.locale_language || this.localeLanguage,
        locale_country: this.queryParams.locale_country || this.localeCountry,
        tracking_numbers: fulfillment.tracking_number,
        carrier: fulfillment.tracking_company,
        order_number: orderNumber,
        version_name: this.versionName,
        t: this.queryParams.t,
      };
      this.initNarvarWidget(options);
      this.addQueryParams(orderNumber, fulfillment.tracking_number, fulfillment.tracking_company);
      this.handleBackBtn();
    } catch (error) {
      console.error(error.message);
      this.updateErrorMessage(
        'Failed to track your order. Please refresh your browser and try again or contact support.',
      );
      this.stopLoading();
    }
  }

  onQueryParamsAdded() {
    try {
      // The below code will automatically parse the values from a URL like
      // http://yourwebsite.com/tracking?tracking_numbers=preview_intransit&carrier=generic
      // and pass them to the embed. Alternatively, these can be overridden and passed
      // directly into the object if the values are available on the client
      if (this.validateQueryParams()) {
        const options = {
          retailer: this.retailer,
          locale_language: this.queryParams.locale_language || this.localeLanguage,
          locale_country: this.queryParams.locale_country || this.localeCountry,
          tracking_numbers: this.queryParams.tracking_number,
          carrier: this.queryParams.carrier,
          order_number: this.queryParams.order_number,
          version_name: this.versionName,
          t: this.queryParams.t,
        };
        this.initNarvarWidget(options);
      }
    } catch (err) {
      console.error(err);
      this.stopLoading();
    }
  }

  validateQueryParams(queryParams) {
    return this.queryParams.tracking_number && this.queryParams.carrier && this.queryParams.order_number;
  }

  startLoading() {
    this.narvarTrackContainerElement.style.display = 'flex';
    this.loadingElement.style.display = 'block';

    this.form.style.display = 'none';
    this.buttonRefreshElement.style.display = 'flex';
    this.trackErrorElement.style.display = 'none';
    this.trackInfoElement.style.display = 'none';
  }

  stopLoading() {
    this.loadingElement.style.display = 'none';
  }

  resetMessages() {
    this.trackErrorElement.style.display = 'none';
    this.trackInfoElement.style.display = 'none';
    this.genericOrderValidator.clear();
    this.orderValidator.clear();
  }

  resetUI() {
    this.hideNarvarWidget();
    this.orderNumberField.value = '';
    this.emailField.value = '';
  }

  updateErrorMessage(message) {
    this.trackErrorElement.style.display = 'block';
    this.trackErrorElement.innerHTML = message;
  }

  updateInfoMessage(message) {
    this.trackInfoElement.style.display = 'block';
    this.trackInfoElement.innerHTML = message;
  }

  initNarvarWidget(options = {}) {
    window.narvar('track', options);
    this.showNarvarWidget();
  }

  addQueryParams(orderNumber, trackingNumber, carrier) {
    window.history.pushState(
      null,
      '',
      `?order_number=${orderNumber}&tracking_number=${trackingNumber}&carrier=${carrier}`,
    );
  }

  showNarvarWidget() {
    this.narvarTrackElement.style.display = 'flex';

    this.form.style.display = 'none';
    this.buttonRefreshElement.style.display = 'flex';
    this.trackErrorElement.style.display = 'none';
    this.trackInfoElement.style.display = 'none';
  }

  hideNarvarWidget() {
    this.narvarTrackElement.style.display = 'none';

    this.form.style.display = 'block';
    this.buttonRefreshElement.style.display = 'none';
    this.trackErrorElement.style.display = 'block';
    this.trackInfoElement.style.display = 'block';
  }

  handleBackBtn() {
    window.addEventListener('popstate', () => this.resetUI());
  }
}

customElements.define('narvar-order-tracker', NarvarOrderTracker);

class NarvarOrderTrackerValidation extends HTMLElement {
  constructor() {
    super();

    this.pattern = this.getAttribute('pattern');
    this.value = this.getAttribute('value');
  }

  validate(value = this.value) {
    const validate = new RegExp(this.pattern);
    return validate.test(value);
  }

  showMsg() {
    this.removeAttribute('hidden');
  }

  clear() {
    this.setAttribute('hidden', true);
  }
}

customElements.define('narvar-order-tracker-validation', NarvarOrderTrackerValidation);
